<template>
  <div class="flex flex-column gap-1">
    <GlobalHeader :title="pageTitle" :description="pageDescription" />

    <div v-if="!cursoStore.loading" class="flex justify-content-end py-3">
      <Button v-if="can('trabalho.create')" label="Novo Trabalho" icon="pi pi-plus" @click="criarNovoTrabalho"
        :loading="criandoTrabalho" class="btn-novo-trabalho" />
    </div>

    <div v-if="cursoStore.loading"
      class="flex flex-column align-items-center justify-content-center p-5 gap-3 surface-card border-1 surface-border border-round">
      <ProgressSpinner />
      <p class="m-0 text-base text-500">Carregando curso...</p>
    </div>

    <div v-else class="border-round border-none">
      <div v-if="!cursoAtual && cursoStore.hasCursos" class="info-alert">
        <i class="pi pi-info-circle"></i>
        <span>
          Exibindo <strong>todos os seus trabalhos</strong>.
          Selecione um curso no menu superior para filtrar.
        </span>
      </div>

      <div v-if="loadingWorks" class="flex flex-column align-items-center gap-3 text-500 text-center py-4">
        <ProgressSpinner />
        <p class="m-0">Carregando trabalhos...</p>
      </div>

      <div v-else-if="filteredWorks.length === 0" class="text-center text-500 py-4">
        <i class="pi pi-folder-open text-4xl mb-2 block"></i>
        <p class="m-0">Voce ainda nao possui trabalhos.</p>
        <small v-if="can('trabalho.create')">
          Clique em "Novo Trabalho" para comecar.
        </small>
      </div>

      <div v-else>
        <div v-for="section in workSections" :key="section.key" class="work-section">
          <div v-if="section.showTitle" class="section-header">
            <span class="section-title">{{ section.title }}</span>
            <span class="section-divider" aria-hidden="true"></span>
          </div>
          <div class="grid">
            <div v-if="section.items.length === 0" class="col-12">
              <p class="text-500 text-sm m-0 py-1">Nenhum trabalho favorito ainda.</p>
            </div>
            <div v-for="work in section.items" :key="work.id" class="col-12 md:col-5 xl:col-4">
              <Card class="work-card">
                <template #title>
                  <div class="flex align-items-center justify-content-between gap-3">
                    <span v-if="getWorkStatus(work).label" class="status-pill" :class="getWorkStatus(work).className">
                      <i v-if="getWorkStatus(work).iconClass" :class="getWorkStatus(work).iconClass" class="mr-2"
                        aria-hidden="true"></i>
                      {{ getWorkStatus(work).label }}
                    </span>
                    <button v-if="!isFinalized(work)" class="favorite-btn" type="button"
                      :class="{ 'is-active': work.is_favorite }" :disabled="isSavingFavorite(work.id)"
                      :aria-label="work.is_favorite ? 'Remover dos favoritos' : 'Favoritar'"
                      :aria-pressed="work.is_favorite" @click.stop="toggleFavorite(work)">
                      <i :class="work.is_favorite ? 'fa-solid fa-star' : 'fa-regular fa-star'" aria-hidden="true"></i>
                    </button>
                  </div>
                  <div class="flex align-items-center flex-wrap gap-2 mt-2">
                    <template v-if="editingWorkId === work.id">
                      <InputText v-model="editingTitle" class="title-input" @keyup.enter="saveTitle(work)"
                        @keyup.escape="cancelEdit" autofocus />
                      <Button icon="fa-solid fa-check" variant="text" severity="success" size="small"
                        :loading="savingTitle" @click="saveTitle(work)" v-tooltip.top="'Salvar'" />
                      <Button icon="fa-solid fa-xmark" variant="text" severity="secondary" size="small"
                        @click="cancelEdit" v-tooltip.top="'Cancelar'" :disabled="savingTitle" />
                    </template>
                    <template v-else>
                      <span class="card-title-text">{{ work.title }}</span>
                    </template>
                  </div>
                </template>

                <template #content>
                  <div class="text-500 text-sm line-height-3">
                    Criado em {{ work.createdAt }}<br />
                    Atualizado em {{ work.updatedAt }}
                  </div>
                </template>

                <template #footer>
                  <div class="flex align-items-center justify-content-between gap-3 mt-auto pt-2">
                    <div class="flex align-items-center gap-2">
                      <Button v-if="can('trabalho.update')" icon="fa-solid fa-pen" variant="text" size="small"
                        class="icon-btn" @click="startEdit(work)" v-tooltip.top="'Editar titulo'" />
                      <Button v-if="podeExcluirTrabalho(work)" icon="fa-solid fa-trash" variant="text" size="small"
                        class="icon-btn icon-btn--danger" @click="confirmDelete(work)" />
                      <Button v-if="work.is_oficial && work.data_fim" icon="pi pi-calendar" variant="text" size="small"
                        class="icon-btn" @click="abrirPeriodoDialog(work)" v-tooltip.top="'Ver Período'" />
                    </div>
                    <Button v-if="can('trabalho.view')" icon="fa-solid fa-folder-open" label="Abrir" size="small"
                      class="btn-abrir" @click="openEditor(work)" />
                  </div>
                </template>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PeriodoDialog v-model:visible="periodoDialogVisible"
      :titulo="periodoDialogWork?.title ? `Período - ${periodoDialogWork.title}` : 'Período do TCC'"
      :data-inicio="periodoDialogWork?.data_inicio" :data-fim="periodoDialogWork?.data_fim"
      :status-prazo="periodoDialogWork?.status_prazo" />

    <TrabalhoEmUsoDialog v-model:visible="trabalhoEmUso.dialogVisivel.value"
      :editando-por="trabalhoEmUso.editandoPor.value" antes-de-abrir
      @abrir="trabalhoEmUso.abrirMesmoAssim" />
  </div>
</template>

<script lang="ts" setup>
import GlobalHeader from "@/components/GlobalHeader.vue";
import { rotaInicialDoPerfil } from "@/utils/rotaInicialDoPerfil";
import PeriodoDialog from "@/components/defesa/PeriodoDialog.vue";
import TrabalhoEmUsoDialog from "@/components/trabalhos/TrabalhoEmUsoDialog.vue";
import { useConferirTrabalhoEmUso } from "@/composables/trabalhos/useConferirTrabalhoEmUso";
import { usePermissions } from "@/composables/usePermissions";
import trabalhoService from "@/services/trabalhos/trabalhoService";
import { useAuthStore } from "@/stores/AuthStore";
import { useCursoStore } from "@/stores/CursoStore";
import Button from "primevue/button";
import Card from "primevue/card";
import InputText from "primevue/inputtext";
import ProgressSpinner from "primevue/progressspinner";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

type Work = {
  id: string;
  title: string;
  is_oficial: boolean;
  is_favorite: boolean;
  curso_id?: number | null;
  curso_codigo?: string;
  createdAt: string;
  updatedAt: string;
  defesa_status?: string | null;
  data_inicio?: string | null;
  data_fim?: string | null;
  status_prazo?: string | null;
};

const searchQuery = ref("");
const loadingWorks = ref(true);
const criandoTrabalho = ref(false);
const works = ref<Work[]>([]);
const isComponentMounted = ref(false);

const editingWorkId = ref<string | null>(null);
const editingTitle = ref("");
const savingTitle = ref(false);
const savingFavoriteIds = ref<Set<string>>(new Set());
const periodoDialogVisible = ref(false);
const periodoDialogWork = ref<Work | null>(null);
// Refs e variáveis reativas

const router = useRouter();
const authStore = useAuthStore();
const cursoStore = useCursoStore();
const trabalhoEmUso = useConferirTrabalhoEmUso(() => authStore.user?.id);
const { can } = usePermissions();
const confirm = useConfirm();
const toast = useToast();

const cursoAtual = computed(() => cursoStore.cursoAtual);

const pageTitle = computed(() => {
  if (authStore.isCoordenador || authStore.isSecretaria || authStore.isCoordenadorTcc) {
    return 'Gerenciar trabalhos';
  }
  return 'Meus Projetos';
});

const pageDescription = computed(() => {
  if (authStore.isCoordenador || authStore.isSecretaria || authStore.isCoordenadorTcc) {
    return 'Gerencie os trabalhos de conclusão de curso dos alunos';
  }
  return 'Gerencie seus trabalhos e crie novos projetos';
});

const filteredWorks = computed(() =>
  works.value.filter((w) =>
    w.title.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
);

const isFinalized = (w: Work) => w.defesa_status?.startsWith('CONCLUIDA_') ?? false;
const activeWorks = computed(() => filteredWorks.value.filter((w) => !isFinalized(w)));
const finalizedWorks = computed(() => filteredWorks.value.filter((w) => isFinalized(w)));
const favoriteWorks = computed(() => activeWorks.value.filter((w) => w.is_favorite));

const workSections = computed(() => {
  const sections: { key: string; title: string; items: Work[]; showTitle: boolean }[] = [];
  const hasFinalized = finalizedWorks.value.length > 0;

  if (activeWorks.value.length > 0) {
    sections.push({ key: "regular", title: "Projetos", items: activeWorks.value, showTitle: true });
    sections.push({ key: "favorites", title: "Favoritos", items: favoriteWorks.value, showTitle: true });
  }

  if (hasFinalized) {
    sections.push({ key: "finalized", title: "Finalizados", items: finalizedWorks.value, showTitle: true });
  }

  if (sections.length === 0 && filteredWorks.value.length > 0) {
    sections.push({ key: "regular", title: "", items: filteredWorks.value, showTitle: false });
  }

  return sections;
});

const loadTrabalhos = async () => {
  if (!authStore.isAuthenticated || !isComponentMounted.value) {
    works.value = [];
    loadingWorks.value = false;
    return;
  }

  try {
    loadingWorks.value = true;

    // Define params: apenas inclui curso_id se houver curso selecionado
    const params: { curso_id?: number } = {};
    if (cursoAtual.value?.id) {
      params.curso_id = cursoAtual.value.id;
    }

    const { data } = await trabalhoService.getTrabalhos(params);

    works.value = (Array.isArray(data) ? data : []).map(
      (t: {
        id: number | string;
        titulo?: string;
        is_oficial?: boolean;
        curso_id?: number | null;
        curso_codigo?: string;
        is_favorite?: boolean;
        favorito?: boolean;
        created_at?: string;
        updated_at?: string;
        created_at_formatted?: string;
        updated_at_formatted?: string;
        defesa?: { id: number; status: string } | null;
      }) => ({
        id: String(t.id),
        title: String(t.titulo || "Sem título"),
        is_oficial: Boolean(t.is_oficial),
        is_favorite: Boolean(t.is_favorite ?? t.favorito ?? false),
        curso_id: t.curso_id || null,
        createdAt: String(t.created_at_formatted || t.created_at || ""),
        updatedAt: String(t.updated_at_formatted || t.updated_at || ""),
        defesa_status: t.defesa?.status || null,
        data_inicio: (t as any).data_inicio || null,
        data_fim: (t as any).data_fim || null,
        status_prazo: (t as any).status_prazo || null,
      })
    );
  } catch (err) {
    console.error("❌ Falha ao carregar trabalhos", err);
    works.value = [];
  } finally {
    loadingWorks.value = false;
  }
};

watch(() => cursoAtual.value?.id, async (newId, oldId) => {
  if (!authStore.isAuthenticated || !isComponentMounted.value) {
    return;
  }

  if (newId !== oldId) {
    await loadTrabalhos();
  }
}, { immediate: false });

onMounted(async () => {
  isComponentMounted.value = true;

  const destino = rotaInicialDoPerfil(authStore.activeRole || authStore.getDefaultRole());
  if (destino !== '/projetos') {
    router.push(destino);
    return;
  }

  if ((authStore.isAluno || authStore.isCoordenador || authStore.isSecretaria || authStore.isCoordenadorTcc || authStore.isOrientador) && !cursoStore.initialized) {
    await cursoStore.initializeCurso();
  }

  await loadTrabalhos();
});

onBeforeUnmount(() => {
  isComponentMounted.value = false;
});

function slugify(text: string) {
  return text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

function openEditor(work: Work) {
  const slug = slugify(work.title);
  void trabalhoEmUso.abrirConferindo(Number(work.id), () => {
    router.push({
      name: "editor",
      params: { id: `${work.id}-${slug}` },
    });
  });
}

function podeExcluirTrabalho(work: Work): boolean {
  if (!can('trabalho.delete')) {
    return false;
  }

  if (work.is_oficial) {
    return false;
  }

  return true;
}

function confirmDelete(work: Work) {
  confirm.require({
    message: `Excluir o trabalho "${work.title}"? Esta ação não pode ser desfeita.`,
    header: "Confirmar Exclusão",
    icon: "pi pi-exclamation-triangle",
    acceptLabel: "Excluir",
    rejectLabel: "Cancelar",
    acceptClass: "p-button-danger",
    accept: async () => {
      try {
        await trabalhoService.deleteTrabalho(Number(work.id));
        works.value = works.value.filter((w) => w.id !== work.id);
        toast.add({
          severity: "success",
          summary: "Excluído",
          detail: "Trabalho removido",
          life: 3000,
        });
      } catch (e) {
        toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao excluir trabalho",
          life: 4000,
        });
      } finally {
        confirm.close();
      }
    },
  });
}

async function criarNovoTrabalho() {
  try {
    criandoTrabalho.value = true;

    const payload: { titulo?: string; curso_id?: number } = {};

    if (cursoAtual.value?.id) {
      payload.curso_id = cursoAtual.value.id;
    }

    const { data } = await trabalhoService.createTrabalho(payload);

    toast.add({
      severity: "success",
      summary: "Trabalho Criado",
      detail: "Trabalho criado com sucesso!",
      life: 3000,
    });

    router.push({
      name: "editor",
      params: { id: data.id }
    });
  } catch (err: any) {
    const errorMsg = err?.response?.data?.message || "Erro ao criar trabalho";
    toast.add({
      severity: "error",
      summary: "Erro",
      detail: errorMsg,
      life: 5000,
    });
  } finally {
    criandoTrabalho.value = false;
  }
}

function startEdit(work: Work) {
  editingWorkId.value = work.id;
  editingTitle.value = work.title;
}

function cancelEdit() {
  editingWorkId.value = null;
  editingTitle.value = "";
}

async function saveTitle(work: Work) {
  const newTitle = editingTitle.value.trim();

  if (!newTitle) {
    toast.add({
      severity: "warn",
      summary: "Aviso",
      detail: "O título não pode estar vazio",
      life: 3000,
    });
    return;
  }

  if (newTitle === work.title) {
    cancelEdit();
    return;
  }

  try {
    savingTitle.value = true;
    await trabalhoService.updateTrabalho(Number(work.id), { titulo: newTitle });

    // Atualiza o título na lista local
    const workIndex = works.value.findIndex(w => w.id === work.id);
    if (workIndex !== -1) {
      works.value[workIndex].title = newTitle;
    }

    toast.add({
      severity: "success",
      summary: "Título atualizado",
      detail: "O título foi alterado com sucesso",
      life: 3000,
    });

    cancelEdit();
  } catch (err: any) {
    const errorMsg = err?.response?.data?.message || "Erro ao atualizar título";
    toast.add({
      severity: "error",
      summary: "Erro",
      detail: errorMsg,
      life: 4000,
    });
  } finally {
    savingTitle.value = false;
  }
}

function isSavingFavorite(workId: string) {
  return savingFavoriteIds.value.has(workId);
}

function setSavingFavorite(workId: string, saving: boolean) {
  const next = new Set(savingFavoriteIds.value);
  if (saving) {
    next.add(workId);
  } else {
    next.delete(workId);
  }
  savingFavoriteIds.value = next;
}

async function toggleFavorite(work: Work) {
  if (isSavingFavorite(work.id)) {
    return;
  }

  const nextValue = !work.is_favorite;
  const previousValue = work.is_favorite;

  work.is_favorite = nextValue;
  setSavingFavorite(work.id, true);

  try {
    await trabalhoService.updateTrabalho(Number(work.id), { is_favorite: nextValue });
  } catch (err: any) {
    work.is_favorite = previousValue;
    const errorMsg = err?.response?.data?.message || "Falha ao atualizar favorito";
    toast.add({
      severity: "error",
      summary: "Erro",
      detail: errorMsg,
      life: 4000,
    });
  } finally {
    setSavingFavorite(work.id, false);
  }
}

function getWorkStatus(work: Work) {
  if (work.defesa_status === 'CONCLUIDA_APROVADO') {
    return {
      label: "Aprovado",
      className: "status-approved",
      iconClass: "fa-solid fa-circle-check",
    };
  }

  if (work.defesa_status === 'CONCLUIDA_REPROVADO') {
    return {
      label: "Reprovado",
      className: "status-rejected",
      iconClass: "fa-solid fa-circle-xmark",
    };
  }

  if (work.defesa_status === 'CONCLUIDA_APROVADO_COM_CORRECOES') {
    return {
      label: "Aprovado com Correções",
      className: "status-corrections",
      iconClass: "fa-solid fa-triangle-exclamation",
    };
  }

  if (work.is_oficial) {
    return {
      label: "Trabalho Academico - Versao Oficial",
      className: "status-official",
      iconClass: "fa-solid fa-user-graduate",
    };
  }

  return {
    label: "",
    className: "",
    iconClass: "",
  };
}

function abrirPeriodoDialog(work: Work) {
  periodoDialogWork.value = work;
  periodoDialogVisible.value = true;
}
</script>

<style scoped>
.btn-novo-trabalho {
  background: var(--p-primary-color) !important;
  border-color: var(--p-primary-color) !important;
  color: #ffffff !important;
}

.btn-novo-trabalho:hover {
  background: var(--p-primary-600) !important;
  border-color: var(--p-primary-600) !important;
}

.work-card {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 10px 15px -3px var(--shadow-color), 0 4px 6px -4px var(--shadow-color);
  background: var(--surface-color);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.25s ease;
  min-height: 220px;
}

.work-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px var(--shadow-color), 0 8px 10px -6px var(--shadow-color);
  border-color: var(--primary-color);
}

:global(.dark) .work-card {
  border-color: var(--border-color) !important;
}

:global(.dark) .work-card:hover {
  border-color: var(--primary-color) !important;
}

.work-card :deep(.p-card-body) {
  padding: 1rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
}

.work-card :deep(.p-card-title) {
  margin: 0;
}

.work-card :deep(.p-card-content) {
  padding: 0;
  flex: 1;
}

.work-card :deep(.p-card-footer) {
  padding: 0;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 0.01em;
}

.status-official {
  background: #1f8f3b;
}

.status-pending {
  background: #b8860b;
}

.status-approved {
  background: #16a34a;
}

.status-rejected {
  background: #dc2626;
}

.status-corrections {
  background: #d97706;
}

.work-section {
  margin-bottom: 1.5rem;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.5rem 0 1rem;
  color: var(--text-color);
  font-weight: 600;
}

.section-title {
  font-size: 1rem;
}

.section-divider {
  flex: 1;
  height: 1px;
  background: var(--border-color);
}

.card-title-text {
  flex: 1;
  min-width: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-color);
  line-height: 1.35;
}

.icon-btn {
  color: var(--p-primary-color) !important;
  padding: 0.25rem !important;
}

.icon-btn:hover {
  color: var(--p-primary-600) !important;
}

.icon-btn--danger {
  color: var(--p-primary-color) !important;
}

.btn-abrir {
  background: var(--p-primary-color) !important;
  border-color: var(--p-primary-color) !important;
  color: #ffffff !important;
  font-weight: 600;
  padding: 0.4rem 1rem !important;
  border-radius: 8px !important;
}

.btn-abrir:hover {
  background: var(--p-primary-600) !important;
  border-color: var(--p-primary-600) !important;
}

.title-input {
  flex: 1;
  min-width: 120px;
  font-size: 1rem;
  padding: 0.35rem 0.5rem;
}

.title-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--primary-color);
}

.favorite-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--surface-color);
  color: var(--p-yellow-600);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.favorite-btn:hover:not(:disabled) {
  border-color: #d2b26c;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(184, 134, 11, 0.18);
}

.favorite-btn.is-active {
  background: color-mix(in srgb, var(--p-yellow-500) 15%, var(--surface-color));
  border-color: var(--p-yellow-400);
}

.favorite-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.favorite-btn:focus-visible {
  outline: 2px solid var(--p-yellow-400);
  outline-offset: 2px;
}
</style>
